<template>
  <div v-if="!isFooterOnly">
    <form @submit.prevent="handleSubmit">
      <div class="field">
        <label class="label">Start Date *</label>
        <div class="control">
          <input
            type="datetime-local"
            class="input"
            v-model="startDateLocal"
            required
          />
        </div>
      </div>

      <div class="field">
        <label class="label">End Date *</label>
        <div class="control">
          <input
            type="datetime-local"
            class="input"
            v-model="endDateLocal"
            required
          />
        </div>
      </div>

      <div class="field">
        <label class="label">Package *</label>
        <div class="control">
          <div class="select">
            <select v-model.number="form.package_id" required>
              <option value="">Select Package</option>
              <option value="1">Basic</option>
              <option value="2">Pro</option>
              <option value="3">Enterprise</option>
            </select>
          </div>
        </div>
      </div>

      <div class="field">
        <label class="label">User Count *</label>
        <div class="control">
          <input
            type="number"
            class="input"
            v-model.number="form.user_count"
            min="1"
            required
          />
        </div>
      </div>

      <div class="field">
        <label class="label">Snapshot Frequency (minutes)</label>
        <div class="control">
          <input
            type="number"
            class="input"
            v-model.number="form.snapshot_frequency"
            min="1"
          />
        </div>
      </div>

      <div class="field">
        <label class="label">Camera Count *</label>
        <div class="control">
          <input
            type="number"
            class="input"
            v-model.number="form.camera_count"
            min="1"
            required
          />
        </div>
      </div>

      <div class="field">
        <label class="label">Project Count *</label>
        <div class="control">
          <input
            type="number"
            class="input"
            v-model.number="form.project_count"
            min="1"
            required
          />
        </div>
      </div>

      <div class="field">
        <label class="label">Monthly Video Count</label>
        <div class="control">
          <input
            type="number"
            class="input"
            v-model.number="form.monthly_video_count"
            min="1"
          />
        </div>
      </div>

      <div class="field">
        <div class="control">
          <label class="checkbox">
            <input type="checkbox" v-model="form.ai_detection" />
            AI Detection
          </label>
        </div>
      </div>

      <div class="field">
        <label class="label">Currency *</label>
        <div class="control">
          <div class="select">
            <select v-model="form.currency" required>
              <option value="">Select Currency</option>
              <option value="USD">USD</option>
              <option value="TL">TL</option>
            </select>
          </div>
        </div>
      </div>

      <div class="field">
        <label class="label">Amount *</label>
        <div class="control">
          <input
            type="number"
            step="0.01"
            class="input"
            v-model.number="form.amount"
            required
          />
        </div>
      </div>
    </form>
  </div>

  <div v-else class="buttons">
    <button class="button" @click="closeModal">Cancel</button>
    <button class="button is-primary" @click="triggerSubmit">
      Save Contract
    </button>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { httpInstance as http } from "@/plugins/http";
import toast from "@/utils/toast";

export default {
  name: "ModalAddContract",
  props: {
    modalData: {
      type: Object,
      default: () => ({}),
    },
    modalIndex: {
      type: Number,
      required: true,
    },
    isFooterOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const now = new Date();
    const oneMonthLater = new Date();
    oneMonthLater.setMonth(now.getMonth() + 1);

    const formatDateForBackend = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}T00:00:00Z`;
    };

    return {
      form: {
        start_date: formatDateForBackend(now),
        end_date: formatDateForBackend(oneMonthLater),
        user_count: 1,
        snapshot_frequency: 1,
        camera_count: 1,
        project_count: 1,
        monthly_video_count: 1,
        ai_detection: false,
        package_id: 1,
        currency: "USD",
        amount: null,
        company_id: null,
      },
    };
  },
  computed: {
    startDateLocal: {
      get() {
        return this.formatToLocal(this.form.start_date);
      },
      set(value) {
        this.form.start_date = this.formatToBackend(value);
      },
    },
    endDateLocal: {
      get() {
        return this.formatToLocal(this.form.end_date);
      },
      set(value) {
        this.form.end_date = this.formatToBackend(value);
      },
    },
  },
  methods: {
    ...mapActions(["closeTopModal"]),
    closeModal() {
      this.closeTopModal();
    },
    triggerSubmit() {
      this.$root.$emit("add-contract-submit");
    },
    formatToLocal(backendDate) {
      if (!backendDate) return "";
      return backendDate.slice(0, -4);
    },
    formatToBackend(localDate) {
      if (!localDate) return "";
      return localDate + ":00Z";
    },
    async handleSubmit() {
      try {
        if (
          !this.form.start_date ||
          !this.form.end_date ||
          !this.form.package_id ||
          !this.form.user_count ||
          !this.form.camera_count ||
          !this.form.project_count ||
          !this.form.currency ||
          !this.form.amount
        ) {
          toast.error("Please fill all required fields");
          return;
        }

        const formData = new FormData();
        this.form.company_id = this.modalData.company_id;

        Object.keys(this.form).forEach((key) => {
          if (key === "next_invoice_date") {
            if (this.form[key]) {
              formData.append(key, this.form[key]);
            }
          } else {
            formData.append(key, this.form[key]);
          }
        });

        const response = await http.post("contracts", formData);

        if (response.status === 200) {
          toast.success("Contract created successfully");
          this.closeModal();
          this.$root.$emit("contract-created");
        }
      } catch (error) {
        console.error("Error creating contract:", error);
        toast.error("Error creating contract");
      }
    },
    initializeForm() {
      if (this.modalData && this.modalData.company_id) {
        this.form.company_id = this.modalData.company_id;
      }
    },
  },
  watch: {
    modalData: {
      handler() {
        this.initializeForm();
      },
      immediate: true,
    },
  },
  mounted() {
    this.initializeForm();

    if (!this.isFooterOnly) {
      this.$root.$on("add-contract-submit", this.handleSubmit);
    }
  },
  beforeDestroy() {
    if (!this.isFooterOnly) {
      this.$root.$off("add-contract-submit", this.handleSubmit);
    }
  },
};
</script>

<style scoped>
.buttons {
  justify-content: flex-end;
}
</style>
